// A standalone, unauthenticated page — the link a patient gets at booking
// (or that keeps working after check-in, same token throughout — see
// ENHANCED_QUEUE_ASSIGNMENT_DESIGN.md §9). Rendered directly by index.js
// whenever the URL carries ?queue_status=<token>. Mobile-first, large text,
// polls every ~30s so a patient checking from their own phone sees a live
// delay banner without needing to refresh.
import { useState, useEffect, useCallback, type CSSProperties } from 'react';
import { useBranding } from '../hooks/useBranding';
import { getQueueStatus } from '../api';

const POLL_MS = 30000;

interface QueueStatus {
  view?: 'upcoming_appointment' | string;
  status: string;
  doctor_name: string;
  time?: string;
  date?: string;
  running_late?: boolean;
  late_minutes?: number;
  cascading_delay?: boolean;
  cascading_delay_minutes?: number;
  patients_ahead?: number | null;
  eta_minutes?: number | null;
  consult_overrunning?: boolean;
  delayed?: boolean;
  delay_minutes?: number;
  /** Why an 'On Hold' consultation is paused: an emergency elsewhere, or waiting for something. */
  hold_kind?: 'emergency' | 'waiting' | null;
  /** The reason staff chose (only for a 'waiting' hold), e.g. "Awaiting lab results". */
  hold_reason?: string | null;
  /** This visit's lab results (only for a 'waiting' hold). */
  lab_total?: number | null;
  lab_ready?: number | null;
}

// Patient-facing wording for a paused consultation, by the reason staff
// picked in HoldConsultationDialog (features/queue). Anything else they typed
// is shown as written. An emergency hold never reveals its stored reason —
// the server sends only hold_kind 'emergency' for it.
function holdMessage(s: QueueStatus): { heading: string; body: string } {
  if (s.hold_kind !== 'waiting') {
    return {
      heading: 'Paused briefly',
      body: 'Your doctor has been called to an emergency. Your consultation is paused and they\'ll be back with you shortly — you keep your place.',
    };
  }
  const reason = (s.hold_reason || '').trim();
  switch (reason.toLowerCase()) {
    case 'awaiting lab results':
      return {
        heading: 'Waiting for lab results',
        body: 'Your doctor is waiting for your lab test results. Your consultation is paused — please stay nearby. You\'ll be called back as soon as the results are ready.',
      };
    case 'awaiting scan / x-ray':
      return {
        heading: 'Waiting for scan / X-ray',
        body: 'Your doctor is waiting for your scan or X-ray. Your consultation is paused — please stay nearby. You\'ll be called back once it is ready.',
      };
    case 'awaiting other report':
      return {
        heading: 'Waiting for a report',
        body: 'Your doctor is waiting for a report. Your consultation is paused — please stay nearby. You\'ll be called back once it is ready.',
      };
    case 'patient stepped out':
      return {
        heading: 'Paused until you return',
        body: 'Your consultation is paused until you are back. Please let the reception desk know when you return and the doctor will continue with you.',
      };
    default:
      return {
        heading: reason || 'Consultation paused',
        body: `Your consultation is paused${reason ? ` — ${reason}` : ''}. Please stay nearby; you'll be called back to continue.`,
      };
  }
}

function fmtDate(d?: string) { return d ? new Date(d).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' }) : ''; }
function fmtTime(t?: string) { return (t || '').slice(0, 5); }

export default function QueueStatusPage({ token }: { token: string }) {
  const { branding } = useBranding();
  const [status, setStatus] = useState<QueueStatus | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    getQueueStatus(token)
      .then((data: QueueStatus) => { setStatus(data); setError(''); })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const wrap: CSSProperties = { minHeight: '100vh', background: '#F3F4F6', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px', fontFamily: "'DM Sans','Segoe UI',system-ui,sans-serif" };
  const card: CSSProperties = { width: '100%', maxWidth: 440, background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,.08)' };

  if (loading) {
    return <div style={wrap}><div style={card}><div style={{ textAlign: 'center', color: '#6B7280', fontSize: 15, padding: 20 }}>Loading…</div></div></div>;
  }

  if (error || !status) {
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={{ textAlign: 'center', fontSize: 36, marginBottom: 10 }}>⚠️</div>
          <div style={{ textAlign: 'center', fontWeight: 700, color: '#991B1B', marginBottom: 6, fontSize: 16 }}>This link isn't valid</div>
          <div style={{ textAlign: 'center', fontSize: 14, color: '#6B7280' }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          {branding.logo_url && <img src={branding.logo_url} alt="" style={{ height: 42, objectFit: 'contain', marginBottom: 8 }} />}
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy, #0B1D33)' }}>{branding.hospital_name}</div>
        </div>

        {status.view === 'upcoming_appointment' ? (
          <>
            <div style={{ textAlign: 'center', fontSize: 13, color: '#6B7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '1px' }}>
              {status.running_late ? 'Check-in overdue' : 'Upcoming appointment'}
            </div>
            <div style={{ textAlign: 'center', fontSize: 22, fontWeight: 800, color: 'var(--teal, #0B5FD6)', marginBottom: 4 }}>{fmtTime(status.time)}</div>
            <div style={{ textAlign: 'center', fontSize: 15, color: '#374151', marginBottom: 14 }}>{fmtDate(status.date)}</div>
            <div style={{ textAlign: 'center', fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{status.doctor_name}</div>
            {status.status !== 'booked' && (
              <div style={{ marginTop: 14, padding: '10px 14px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 10, textAlign: 'center', fontSize: 13, color: '#991B1B' }}>
                This appointment was {status.status} — please contact reception.
              </div>
            )}
            {status.status === 'booked' && status.running_late && (
              <div style={{ marginTop: 14, padding: '10px 14px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 10, textAlign: 'center', fontSize: 13, color: '#92400E' }}>
                This appointment is about {status.late_minutes} min overdue for check-in. Please check in as soon as possible, or contact reception if you need to reschedule.
              </div>
            )}
            {status.status === 'booked' && status.cascading_delay && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, textAlign: 'center', fontSize: 13, color: '#9A3412' }}>
                Heads up — {status.doctor_name}'s schedule is running about {status.cascading_delay_minutes} min behind today. Your appointment may be delayed; this page will update automatically.
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ textAlign: 'center', fontSize: 13, color: '#6B7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '1px' }}>
              {status.status === 'Completed' ? 'Consultation complete'
                : status.status === 'In Consultation' ? 'With the doctor now'
                : status.status === 'On Hold' ? holdMessage(status).heading
                : 'Waiting in queue'}
            </div>
            <div style={{ textAlign: 'center', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>{status.doctor_name}</div>

            {status.status === 'Waiting' && (
              <>
                {/* Nobody ahead and no wait to quote is "you're next", not a
                    pair of zeros — two big 0s next to a "running behind"
                    banner reads as a broken page. */}
                {status.patients_ahead === 0 && status.eta_minutes === 0 ? (
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--teal, #0B5FD6)' }}>You're next</div>
                    <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>Please stay nearby — you'll be called shortly.</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 16 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--teal, #0B5FD6)' }}>{status.patients_ahead ?? '—'}</div>
                      <div style={{ fontSize: 12, color: '#6B7280' }}>ahead of you</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--teal, #0B5FD6)' }}>
                        {status.eta_minutes ?? '—'}{status.consult_overrunning && status.eta_minutes != null ? '+' : ''}
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7280' }}>est. minutes</div>
                    </div>
                  </div>
                )}
                {status.consult_overrunning && (
                  <div style={{ marginBottom: 10, padding: '10px 14px', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, textAlign: 'center', fontSize: 13, color: '#9A3412' }}>
                    The consultation in progress is running longer than usual, so this estimate may grow.
                  </div>
                )}
                {status.delayed && (
                  <div style={{ padding: '10px 14px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 10, textAlign: 'center', fontSize: 13, color: '#92400E' }}>
                    Running about {status.delay_minutes} min behind schedule today
                  </div>
                )}
              </>
            )}
            {status.status === 'In Consultation' && (
              <div style={{ textAlign: 'center', fontSize: 14, color: '#374151' }}>You're being seen right now.</div>
            )}
            {/* A paused consultation, said plainly by its reason — an
                emergency elsewhere (the doctor returns to them), or waiting
                for results / the patient — rather than a status word nobody
                outside the hospital would read as reassuring. */}
            {status.status === 'On Hold' && (
              <>
                <div style={{ textAlign: 'center', fontSize: 14, color: '#374151' }}>
                  {holdMessage(status).body}
                </div>
                {status.hold_kind === 'waiting' && (status.lab_total ?? 0) > 0 && (
                  <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 10, textAlign: 'center', fontSize: 13,
                    background: (status.lab_ready ?? 0) >= (status.lab_total ?? 0) ? '#ECFDF5' : '#F3F4F6',
                    border: `1px solid ${(status.lab_ready ?? 0) >= (status.lab_total ?? 0) ? '#A7F3D0' : '#E5E7EB'}`,
                    color: (status.lab_ready ?? 0) >= (status.lab_total ?? 0) ? '#065F46' : '#374151' }}>
                    🧪 {(status.lab_ready ?? 0) >= (status.lab_total ?? 0)
                      ? 'Your test results are ready — you\'ll be called back shortly.'
                      : `${status.lab_ready ?? 0} of ${status.lab_total} test results ready`}
                  </div>
                )}
              </>
            )}
            {status.status === 'Completed' && (
              <div style={{ textAlign: 'center', fontSize: 14, color: '#374151' }}>Your consultation is complete. Thank you!</div>
            )}
          </>
        )}

        <div style={{ textAlign: 'center', fontSize: 11, color: '#9CA3AF', marginTop: 24 }}>
          This page updates automatically — no need to refresh.
        </div>
      </div>
    </div>
  );
}
